import { useState } from 'react'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { GlassmorphicCard } from '@/components/ui/glassmorphic-card'
import {
    TrendingUp, Target, Award, Clock,
    BarChart2, PieChart, Activity, ChevronRight, ArrowUp, ArrowDown
} from 'lucide-react'

// Mock performance data
const performanceData = {
    overallScore: 645,
    maxScore: 720,
    percentile: 94.5,
    accuracy: 86.3,
    avgTimePerQuestion: '1m 12s',
    testsCompleted: 12,
    improvementRate: 4.2,
    subjectPerformance: [
        { subject: 'Physics', score: 155, maxScore: 180, accuracy: 86, trend: 'up', change: 5 },
        { subject: 'Chemistry', score: 162, maxScore: 180, accuracy: 90, trend: 'up', change: 3 },
        { subject: 'Biology', score: 328, maxScore: 360, accuracy: 91, trend: 'stable', change: 0 },
    ],
    weeklyProgress: [
        { week: 'Week 1', score: 580 },
        { week: 'Week 2', score: 595 },
        { week: 'Week 3', score: 610 },
        { week: 'Week 4', score: 620 },
        { week: 'Week 5', score: 635 },
        { week: 'Week 6', score: 645 },
    ],
    chapterWise: {
        physics: [
            { chapter: 'Mechanics', accuracy: 92, attempted: 45 },
            { chapter: 'Thermodynamics', accuracy: 85, attempted: 32 },
            { chapter: 'Optics', accuracy: 78, attempted: 28 },
            { chapter: 'Electrostatics', accuracy: 88, attempted: 40 },
        ],
        chemistry: [
            { chapter: 'Organic Chemistry', accuracy: 88, attempted: 55 },
            { chapter: 'Inorganic Chemistry', accuracy: 92, attempted: 42 },
            { chapter: 'Physical Chemistry', accuracy: 85, attempted: 38 },
        ],
        biology: [
            { chapter: 'Human Physiology', accuracy: 94, attempted: 60 },
            { chapter: 'Genetics', accuracy: 90, attempted: 48 },
            { chapter: 'Ecology', accuracy: 88, attempted: 35 },
            { chapter: 'Cell Biology', accuracy: 92, attempted: 50 },
        ],
    },
    recentTests: [
        { name: 'Mock Test 6', date: '2 days ago', score: 645, maxScore: 720 },
        { name: 'Mock Test 5', date: '1 week ago', score: 635, maxScore: 720 },
        { name: 'Mock Test 4', date: '2 weeks ago', score: 620, maxScore: 720 },
    ],
}

export function PerformanceAnalyticsView() {
    const navigate = useNavigate()
    const [selectedSubject, setSelectedSubject] = useState<'physics' | 'chemistry' | 'biology'>('physics')


    // Calculate max for progress chart
    const maxWeeklyScore = Math.max(...performanceData.weeklyProgress.map(w => w.score))

    return (
        <div className="space-y-4 md:space-y-6 pb-24 lg:pb-0">
            {/* Header */}
            <div className="flex items-center justify-between">
                <h2 className="text-xl md:text-2xl font-bold text-theme-primary">Performance Analytics</h2>
                <span className="text-sm text-theme-secondary">{performanceData.testsCompleted} tests analyzed</span>
            </div>

            {/* Overall Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <GlassmorphicCard className="p-4">
                    <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-blue to-accent-gold flex items-center justify-center">
                            <Award className="h-5 w-5 text-white" />
                        </div>
                        <div>
                            <p className="text-2xl font-bold text-theme-primary">{performanceData.overallScore}</p>
                            <p className="text-xs text-theme-secondary">Avg Score</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-1 text-xs text-green-500">
                        <ArrowUp className="h-3 w-3" />
                        <span>+{performanceData.improvementRate}% this month</span>
                    </div>
                </GlassmorphicCard>

                <GlassmorphicCard className="p-4">
                    <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center">
                            <Target className="h-5 w-5 text-white" />
                        </div>
                        <div>
                            <p className="text-2xl font-bold text-theme-primary">{performanceData.accuracy}%</p>
                            <p className="text-xs text-theme-secondary">Accuracy</p>
                        </div>
                    </div>
                </GlassmorphicCard>

                <GlassmorphicCard className="p-4">
                    <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center">
                            <Activity className="h-5 w-5 text-white" />
                        </div>
                        <div>
                            <p className="text-2xl font-bold text-theme-primary">{performanceData.percentile}%</p>
                            <p className="text-xs text-theme-secondary">Percentile</p>
                        </div>
                    </div>
                </GlassmorphicCard>

                <GlassmorphicCard className="p-4">
                    <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center">
                            <Clock className="h-5 w-5 text-white" />
                        </div>
                        <div>
                            <p className="text-2xl font-bold text-theme-primary">{performanceData.avgTimePerQuestion}</p>
                            <p className="text-xs text-theme-secondary">Avg Time/Q</p>
                        </div>
                    </div>
                </GlassmorphicCard>
            </div>

            {/* Progress Chart & Subject Performance */}
            <div className="grid lg:grid-cols-2 gap-4 md:gap-6">
                {/* Weekly Progress */}
                <GlassmorphicCard className="p-4 md:p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="font-bold text-theme-primary flex items-center gap-2">
                            <BarChart2 className="h-5 w-5 text-primary-blue" />
                            Weekly Progress
                        </h3>
                        <span className="text-xs text-green-500 flex items-center gap-1">
                            <TrendingUp className="h-3.5 w-3.5" />
                            +11% overall
                        </span>
                    </div>
                    <div className="flex items-end gap-2 h-40">
                        {performanceData.weeklyProgress.map((week, i) => (
                            <div key={week.week} className="flex-1 flex flex-col items-center gap-2">
                                <span className="text-xs font-bold text-theme-primary">{week.score}</span>
                                <motion.div
                                    initial={{ height: 0 }}
                                    animate={{ height: `${(week.score / maxWeeklyScore) * 100}%` }}
                                    transition={{ delay: i * 0.1, duration: 0.4 }}
                                    className="w-full bg-gradient-to-t from-primary-blue to-accent-gold rounded-t-lg min-h-[20px]"
                                />
                                <span className="text-[10px] text-theme-secondary">{week.week.split(' ')[1]}</span>
                            </div>
                        ))}
                    </div>
                </GlassmorphicCard>

                {/* Subject Performance */}
                <GlassmorphicCard className="p-4 md:p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="font-bold text-theme-primary flex items-center gap-2">
                            <PieChart className="h-5 w-5 text-primary-blue" />
                            Subject Performance
                        </h3>
                    </div>
                    <div className="space-y-4">
                        {performanceData.subjectPerformance.map((subject) => (
                            <div key={subject.subject}>
                                <div className="flex items-center justify-between mb-2">
                                    <span className="font-medium text-theme-primary">{subject.subject}</span>
                                    <div className="flex items-center gap-2">
                                        <span className="text-sm font-bold text-primary-blue">{subject.score}/{subject.maxScore}</span>
                                        {subject.trend === 'up' && (
                                            <span className="text-xs text-green-500 flex items-center gap-0.5">
                                                <ArrowUp className="h-3 w-3" />+{subject.change}%
                                            </span>
                                        )}
                                        {subject.trend === 'down' && (
                                            <span className="text-xs text-red-500 flex items-center gap-0.5">
                                                <ArrowDown className="h-3 w-3" />-{subject.change}%
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <div className="h-3 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                                    <motion.div
                                        initial={{ width: 0 }}
                                        animate={{ width: `${(subject.score / subject.maxScore) * 100}%` }}
                                        transition={{ duration: 0.6 }}
                                        className={`h-full bg-gradient-to-r ${subject.subject === 'Physics' ? 'from-blue-500 to-cyan-500' :
                                            subject.subject === 'Chemistry' ? 'from-green-500 to-emerald-500' :
                                                'from-purple-500 to-pink-500'
                                            } rounded-full`}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                </GlassmorphicCard>
            </div>

            {/* Chapter-wise Analysis */}
            <GlassmorphicCard className="p-4 md:p-6">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="font-bold text-theme-primary">Chapter-wise Analysis</h3>
                    <div className="flex bg-slate-100 dark:bg-slate-800 p-1 rounded-lg">
                        {(['physics', 'chemistry', 'biology'] as const).map((subject) => (
                            <button
                                key={subject}
                                onClick={() => setSelectedSubject(subject)}
                                className={`px-3 py-1.5 text-xs font-medium rounded-md capitalize transition-all ${selectedSubject === subject
                                    ? 'bg-white dark:bg-slate-700 shadow text-primary-blue'
                                    : 'text-slate-500 hover:text-slate-700'
                                    }`}
                            >
                                {subject}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-3">
                    {performanceData.chapterWise[selectedSubject].map((chapter, i) => (
                        <motion.div
                            key={chapter.chapter}
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: i * 0.05 }}
                            className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl flex items-center justify-between"
                        >
                            <div>
                                <p className="font-medium text-theme-primary text-sm">{chapter.chapter}</p>
                                <p className="text-xs text-theme-secondary">{chapter.attempted} questions attempted</p>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className={`w-12 h-12 rounded-full flex items-center justify-center text-sm font-bold ${chapter.accuracy >= 90 ? 'bg-green-100 text-green-600' :
                                    chapter.accuracy >= 80 ? 'bg-blue-100 text-blue-600' :
                                        chapter.accuracy >= 70 ? 'bg-yellow-100 text-yellow-600' :
                                            'bg-red-100 text-red-600'
                                    }`}>
                                    {chapter.accuracy}%
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </GlassmorphicCard>

            {/* Recent Tests */}
            <GlassmorphicCard className="p-4 md:p-6">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="font-bold text-theme-primary flex items-center gap-2">
                        <Award className="h-5 w-5 text-primary-blue" />
                        Recent Tests
                    </h3>
                    <button className="text-sm text-primary-blue hover:underline flex items-center gap-1">
                        View All <ChevronRight className="h-4 w-4" />
                    </button>
                </div>
                <div className="space-y-3">
                    {performanceData.recentTests.map((test, i) => {
                        const prevScore = performanceData.recentTests[i + 1]?.score || test.score
                        const change = ((test.score - prevScore) / prevScore * 100).toFixed(1)
                        const isImproving = test.score > prevScore

                        return (
                            <button
                                key={i}
                                onClick={() => navigate('/student/test/results')}
                                className="w-full flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 transition-all group"
                            >
                                <div className="flex items-center gap-3">
                                    <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-primary-blue to-accent-gold flex items-center justify-center text-white font-bold">
                                        {i + 1}
                                    </div>
                                    <div className="text-left">
                                        <p className="font-medium text-theme-primary text-sm group-hover:text-primary-blue transition-colors">{test.name}</p>
                                        <p className="text-xs text-theme-secondary">{test.date}</p>
                                    </div>
                                </div>
                                <div className="text-right flex items-center gap-3">
                                    {i < performanceData.recentTests.length - 1 && (
                                        <div className={`flex items-center gap-1 text-xs ${isImproving ? 'text-green-500' : 'text-red-500'}`}>
                                            {isImproving ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />}
                                            {Math.abs(parseFloat(change))}%
                                        </div>
                                    )}
                                    <div>
                                        <p className="font-bold text-primary-blue">{test.score}</p>
                                        <p className="text-xs text-theme-secondary">/{test.maxScore}</p>
                                    </div>
                                    <ChevronRight className="h-4 w-4 text-slate-400 group-hover:text-primary-blue transition-colors" />
                                </div>
                            </button>
                        )
                    })}
                </div>
            </GlassmorphicCard>
        </div>
    )
}
